import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, ChevronLeft, ChevronUp, Loader2, Search } from "lucide-react";
import ProductCard from "../components/ProductCard";
import { useCatalog, useCategories } from "../hooks/useCatalog";
import { resolvePhotoUrl } from "../lib/photos";
import { productSubcategories } from "../lib/product";
import usePageMeta from "../hooks/usePageMeta";

const VISIBLE_INITIAL = 18;

// SEO per category (keyed by exact category id from the API).
// Titles <= 60 chars, descriptions <= 155 chars with price/delivery/COD arguments.
const CATEGORY_SEO = {
    "coques": {
        title: "Coques iPhone au Maroc – Prix en DH | Atelier AX",
        description:
            "Coques et pochettes iPhone (17, 16, 15…) : transparentes, MagSafe, silicone. Prix en DH, livraison 24-72h partout au Maroc, paiement à la livraison.",
    },
    "coques-samsung": {
        title: "Coques Samsung au Maroc – Prix en DH | Atelier AX",
        description:
            "Coques et pochettes Samsung Galaxy S et A : transparentes, silicone, antichoc. Prix en DH, livraison 24-72h partout au Maroc, paiement à la livraison.",
    },
    "coques-airpods": {
        title: "Coques AirPods au Maroc – Prix en DH | Atelier AX",
        description:
            "Coques et étuis AirPods : silicone, transparents, antichoc. Prix en DH, livraison 24-72h partout au Maroc, paiement à la livraison.",
    },
    "coque-personnalisee": {
        title: "Coque personnalisée iPhone & Samsung – Maroc | Atelier AX",
        description:
            "Coque personnalisée avec votre photo ou prénom pour iPhone et Samsung. Prix en DH, livraison partout au Maroc, paiement à la livraison.",
    },
    "verre-trempe-hydrogel": {
        title: "Verre trempé & film hydrogel – Pose dès 50 DH | Atelier AX",
        description:
            "Verre trempé et film hydrogel pour iPhone et Samsung, pose en boutique dès 50 DH. Livraison partout au Maroc, paiement à la livraison.",
    },
    "chargeurs": {
        title: "Chargeurs rapides iPhone & Samsung – Maroc | Atelier AX",
        description:
            "Chargeurs rapides 20W, 25W, 40W, 45W (PD, GaN) pour iPhone et Samsung. Prix en DH, livraison 24-72h partout au Maroc, paiement à la livraison.",
    },
    "cables-hubs": {
        title: "Câbles USB-C, Lightning & hubs – Maroc | Atelier AX",
        description:
            "Câbles USB-C, Lightning 60W, CarPlay et hubs 6-en-1. Prix en DH, livraison 24-72h partout au Maroc, paiement à la livraison.",
    },
    "power-bank": {
        title: "Batterie externe / Power Bank au Maroc | Atelier AX",
        description:
            "Batteries externes / Power Bank 10000 et 20000 mAh, MagSafe. Prix en DH, livraison 24-72h partout au Maroc, paiement à la livraison.",
    },
    "accessoires-voiture": {
        title: "Accessoires voiture pour téléphone – Maroc | Atelier AX",
        description:
            "Chargeurs allume-cigare, supports et accessoires voiture pour téléphone. Prix en DH, livraison partout au Maroc, paiement à la livraison.",
    },
    "accessoires-audio-gadgets": {
        title: "Accessoires audio & gadgets – Maroc | Atelier AX",
        description:
            "Écouteurs, AirTag et gadgets mobiles au meilleur prix en DH. Livraison 24-72h partout au Maroc, paiement à la livraison.",
    },
};

const CategoryPage = () => {
    const { categoryId } = useParams();
    const [search, setSearch] = useState("");
    const [subcategoryFilter, setSubcategoryFilter] = useState(null);
    const [showAll, setShowAll] = useState(false);
    const gridRef = useRef(null);
    const didMount = useRef(false);

    useEffect(() => {
        if (!didMount.current) {
            didMount.current = true;
            return;
        }
        if (subcategoryFilter && gridRef.current) {
            const y = gridRef.current.getBoundingClientRect().top + window.scrollY - 100;
            window.scrollTo({ top: y, behavior: "smooth" });
        }
    }, [subcategoryFilter]);

    const { data: categories = [], isLoading: categoriesLoading } = useCategories();
    const { data: catalogData, isLoading: catalogLoading, error: catalogError } = useCatalog();

    const category = useMemo(
        () => categories.find((c) => c.id === categoryId),
        [categories, categoryId],
    );

    const seo = category ? CATEGORY_SEO[category.id] : null;

    usePageMeta({
        title: seo
            ? seo.title
            : category
              ? `${category.label} — Prix en DH au Maroc | Atelier AX`
              : "Catégorie — Atelier AX",
        description: seo
            ? seo.description
            : category
              ? `${category.label} au meilleur prix en DH. Livraison 24-72h partout au Maroc, paiement à la livraison — Atelier AX.`
              : "Catégorie Atelier AX.",
        path: `/categorie/${categoryId}`,
    });

    const products = useMemo(() => catalogData?.items || [], [catalogData]);

    const filtered = useMemo(() => {
        if (!category) return [];
        let result = products.filter((p) => p.category === category.label);

        const q = search.trim().toLowerCase();
        if (q) {
            result = result.filter((p) => {
                const nameMatch = p.name?.toLowerCase().includes(q);
                const specMatch = (p.specs || []).some((s) => s.toLowerCase().includes(q));
                const subcategoryMatch = productSubcategories(p).some((s) =>
                    s.toLowerCase().includes(q),
                );
                return nameMatch || specMatch || subcategoryMatch;
            });
        }

        if (subcategoryFilter) {
            result = result.filter((p) =>
                productSubcategories(p).includes(subcategoryFilter),
            );
        }

        // Top ventes en tête (tri stable) : garantit que le badge "Top vente"
        // est visible dans le prérendu (VISIBLE_INITIAL) comme côté client.
        result = [...result].sort(
            (a, b) => (b.top_selling ? 1 : 0) - (a.top_selling ? 1 : 0)
        );

        return result;
    }, [products, category, subcategoryFilter, search]);

    const isLoading = categoriesLoading || catalogLoading;

    if (!isLoading && !category) {
        return <Navigate to="/" replace />;
    }

    const visible = showAll ? filtered : filtered.slice(0, VISIBLE_INITIAL);
    const hasMore = filtered.length > VISIBLE_INITIAL;
    const subcategories = (category?.subcategories || [])
        .filter((s) => s.active)
        .sort((a, b) => a.sort_order - b.sort_order);
    const photo = resolvePhotoUrl(category?.photo);

    return (
        <section className="relative min-h-screen bg-[#FBFBFD] pb-24 md:pb-32">
            <div className="max-w-[1320px] mx-auto px-4 sm:px-6 md:px-10 pt-6 md:pt-8">
                <Link
                    to="/"
                    className="inline-flex items-center gap-1 text-[13px] font-medium text-neutral-500 transition-colors hover:text-neutral-900"
                >
                    <ChevronLeft className="w-4 h-4" />
                    Retour à l'accueil
                </Link>

                {isLoading ? (
                    <div className="mt-20 flex flex-col items-center justify-center gap-4 text-neutral-500">
                        <Loader2 className="w-8 h-8 animate-spin text-[#d4af37]" />
                        <span className="text-sm font-medium">Chargement de la catégorie…</span>
                    </div>
                ) : (
                    <>
                        {/* Category header */}
                        <div className="mt-5 flex items-center gap-4 md:gap-6">
                            {photo && (
                                <div className="h-16 w-16 md:h-20 md:w-20 shrink-0 overflow-hidden rounded-2xl border border-black/[0.06] bg-white shadow-sm">
                                    <img
                                        src={photo}
                                        alt={category.label}
                                        className="h-full w-full object-cover"
                                    />
                                </div>
                            )}
                            <div>
                                <span className="dp-eyebrow">Catégorie</span>
                                <h1 className="mt-1 font-display text-3xl md:text-4xl font-semibold tracking-tight text-[#1C1C1E]">
                                    {category.label}
                                </h1>
                                <p className="mt-1 text-[13px] text-neutral-500">
                                    {products.filter((p) => p.category === category.label).length} produit
                                    {products.filter((p) => p.category === category.label).length > 1 ? "s" : ""}
                                </p>
                            </div>
                        </div>

                        {/* Search + subcategories */}
                        <div className="mt-8 flex flex-col gap-4">
                            <div className="relative flex items-center">
                                <Search className="absolute left-3.5 w-3.5 h-3.5 text-neutral-400 pointer-events-none" />
                                <input
                                    type="text"
                                    value={search}
                                    onChange={(e) => {
                                        setSearch(e.target.value);
                                        setShowAll(false);
                                    }}
                                    placeholder="Rechercher dans cette catégorie…"
                                    className="inline-flex items-center font-medium text-[12.5px] py-2 pl-10 pr-4 min-w-[220px] md:min-w-[280px] rounded-full border border-black/[0.12] bg-transparent text-neutral-900 placeholder:text-neutral-500 cursor-text focus:outline-none focus:ring-2 focus:ring-[#d4af37]/20 focus:border-[#d4af37]/40"
                                />
                            </div>

                            <AnimatePresence>
                                {subcategories.length > 0 && (
                                    <motion.div
                                        initial={{ opacity: 0, y: -8 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -8 }}
                                        transition={{ duration: 0.2 }}
                                        className="flex flex-wrap items-center gap-2"
                                    >
                                        <span className="text-[12px] text-neutral-500 mr-1">
                                            Sous-catégories :
                                        </span>
                                        {subcategories.map((s) => {
                                            const count = products.filter(
                                                (p) =>
                                                    p.category === category.label &&
                                                    productSubcategories(p).includes(s.label),
                                            ).length;
                                            return (
                                                <button
                                                    key={s.id}
                                                    type="button"
                                                    onClick={() => {
                                                        setSubcategoryFilter((prev) =>
                                                            prev === s.label ? null : s.label,
                                                        );
                                                        setShowAll(false);
                                                    }}
                                                    className={`dp-btn text-[11.5px] py-1.5 px-3 rounded-full ${
                                                        subcategoryFilter === s.label
                                                            ? "bg-[#8b5cf6] text-white border-[#8b5cf6]"
                                                            : "dp-btn-ghost"
                                                    }`}
                                                >
                                                    {s.label} • {count}
                                                </button>
                                            );
                                        })}
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>

                        {/* Error state */}
                        {catalogError && (
                            <div className="mt-10 rounded-2xl border border-red-200 bg-red-50 p-6 text-red-700">
                                <p className="font-medium">Impossible de charger le catalogue.</p>
                                <p className="text-sm mt-1">{catalogError.message}</p>
                            </div>
                        )}

                        {/* Grid */}
                        <div ref={gridRef} className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-x-4 sm:gap-x-6 md:gap-x-8 gap-y-10 sm:gap-y-12 md:gap-y-14">
                            <AnimatePresence mode="popLayout">
                                {visible.map((p, i) => (
                                    <ProductCard key={p.id} product={p} index={i} />
                                ))}
                            </AnimatePresence>
                        </div>

                        {/* Empty state */}
                        {!isLoading && visible.length === 0 && (
                            <div className="mt-16 text-center text-neutral-500">
                                Aucun produit ne correspond à{" "}
                                {search.trim() ? "cette recherche" : "ce filtre"}.
                            </div>
                        )}

                        {/* Voir tout */}
                        {hasMore && (
                            <div className="mt-12 flex flex-col items-center gap-3">
                                <button
                                    type="button"
                                    onClick={() => setShowAll((v) => !v)}
                                    className="dp-btn dp-btn-ghost"
                                >
                                    {showAll ? (
                                        <>
                                            <ChevronUp className="w-4 h-4" />
                                            Réduire
                                        </>
                                    ) : (
                                        <>
                                            Voir tout ({filtered.length})
                                            <ChevronDown className="w-4 h-4" />
                                        </>
                                    )}
                                </button>
                                <div className="text-[12px] font-mono-tech tracking-[0.2em] uppercase text-neutral-400">
                                    {visible.length} / {filtered.length} produits affichés
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </section>
    );
};

export default CategoryPage;
